import React, { useEffect, useState } from "react";
import LmsTable from "../ui/LmsTable";
import {
  useParseRouteActions,
  useFetchData,
  useForm,
  usePromiseAll,
} from "../../../helpers/GlobalHelper";
import LmsFormGenerator, { BCFormController } from "../ui/LmsFormGenerator";

const FUND_MANAGER_ENDPOINT = "funds/fund-manager";
const ORGANIZATION_FUNDS_ENDPOINT = "metadata/list-organization-funds";

const formatDropdownOptions = (options) =>
  options.map(({ id, display }, i) => ({
    value: id || "",
    key: `${id}${i}`,
    text: display || "",
  }));

const FundsManagersTable = () => (
  <LmsTable
    sortable={["Created On"]}
    apiSuffix="funds/fund-managers"
    deleteSuffix={FUND_MANAGER_ENDPOINT}
    actionButtons={["add", "edit", "remove", "export"]}
    columnLabelsDictionary={{ organizationContact: "Contact", fundOrganization: "Fund" }}
  />
);

const FundManagerForm = ({ id, history }) => {
  const [{ fetchData }] = useFetchData();
  const [isLoading, setIsLoading] = useState(false);
  const managerDataEndpoint = id ? `${FUND_MANAGER_ENDPOINT}/${id}` : "";
  const [data, setData] = useState<any>({
    fundOrganizationId: "",
    managerData: { isActive: true },
    dropdownOptions: { fundOrganizationId: [], organizationContactId: [] },
  });

  const {
    promisesData = null,
    promisesLoading,
    promisesError,
  } = usePromiseAll([ORGANIZATION_FUNDS_ENDPOINT, ...(id ? [managerDataEndpoint] : [])], {
    isReady: true,
  });

  const getContactOptions = async (fundOrganizationId, data) => {
    setIsLoading(true);
    const extraOptions = data.organizationContactId
      ? [{ id: data.organizationContactId, display: data.organizationContact }]
      : [];

    try {
      const { data } = await fetchData(`metadata/list-contacts/${fundOrganizationId}`, {
        skipCache: true,
      });
      return formatDropdownOptions(extraOptions.concat(data || []));
    } catch {
      return formatDropdownOptions(extraOptions);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeFundOrganization = async ({ value }) => {
    if (value !== data.fundOrganizationId) {
      const contactList = await getContactOptions(value, data.managerData);
      setData((prevData) => ({
        ...prevData,
        fundOrganizationId: value,
        dropdownOptions: {
          ...prevData.dropdownOptions,
          organizationContactId: contactList,
        },
      }));
    }
  };

  const handleSubmit = (data, options) => {
    options.handleSubmit(FUND_MANAGER_ENDPOINT, {
      method: id ? "PUT" : "POST",
      data: {
        id: data.id,
        isActive: data.isActive,
        fundOrganizationId: data.fundOrganizationId,
        organizationContactId: data.organizationContactId,
      },
      useFormInstance: { actionName: options.actionName },
      buttonText: options.buttonText,
      actionName: options.actionName,
    });
  };

  const myForm = new BCFormController(data.managerData, {
    deleteSuffix: FUND_MANAGER_ENDPOINT,
    onSubmitSuccess: (_, options) => {
      if (options && "actionName" in options && options.actionName === "save-new") {
        useFormInstance.setInputs({ isActive: true });
      }
    },
  });

  const useFormInstance = useForm(data.managerData, {
    myForm,
    allDropdownOptions: data.dropdownOptions,
  }) as any;

  myForm.addCenteredGroup([myForm.getCheckboxField("Is Active", { inline: true })]);

  myForm.addCenteredGroup([
    myForm.getDropdownField("Fund", "", {
      required: true,
      name: "fundOrganizationId",
      onFieldChanged: handleChangeFundOrganization,
    }),
  ]);

  myForm.addCenteredGroup([
    myForm.getDropdownField("Contact", "", {
      required: true,
      name: "organizationContactId",
      isDisabledOnFunc: ({ inputs }) => !inputs.fundOrganizationId,
    }),
  ]);

  useEffect(() => {
    if (promisesData) {
      if (!promisesData[managerDataEndpoint] && id) history.replace("/fund-managers/add");
      (async () => {
        const contactList = promisesData[managerDataEndpoint]
          ? await getContactOptions(
              promisesData[managerDataEndpoint].fundOrganizationId,
              promisesData[managerDataEndpoint]
            )
          : [];

        setData((prevData) => {
          const managerData = { ...prevData.managerData, ...promisesData[managerDataEndpoint] };
          return {
            ...prevData,
            managerData,
            fundOrganizationId: managerData.fundOrganizationId || "",
            dropdownOptions: {
              organizationContactId: contactList,
              fundOrganizationId: formatDropdownOptions(promisesData[ORGANIZATION_FUNDS_ENDPOINT]),
            },
          };
        });
      })();
    }
  }, [promisesData]);

  return (
    <LmsFormGenerator
      myForm={myForm}
      error={promisesError}
      handleSubmit={handleSubmit}
      useFormInstance={useFormInstance}
      isLoading={isLoading || promisesLoading}
      actions={
        id
          ? ["save", "save-close", "delete", "close"]
          : ["save-new", "save-continue", "save-close", "close"]
      }
    />
  );
};

const LmsFundsFundManagers = ({ params = {}, history }) => {
  const { isMainPage, isAdd, isEdit, isActionAllowed, id } = useParseRouteActions(params);

  if (!isActionAllowed) {
    history.goBack();
  }

  return (
    <>
      {isMainPage && <FundsManagersTable />}
      {(isAdd || isEdit) && <FundManagerForm id={id} history={history} />}
    </>
  );
};

export default LmsFundsFundManagers;
